/**
 * Collaboration Chat System
 * Real-time chat for users in a shared visualization session
 * Messages stored in Redis, broadcast via WebSocket
 */

class CollaborationChat {
    constructor(websocket, sessionId, userId, userName) {
        this.ws = websocket;
        this.sessionId = sessionId;
        this.userId = userId;
        this.userName = userName;

        this.messages = [];
        this.isOpen = false;
        this.unreadCount = 0;
        this.lastReadTimestamp = null;
        this.replyingTo = null;
        this.mentionUser = null;
        this.typingUsers = new Map(); // userId -> {name, timeout}

        this.createChatUI();
        this.setupEventListeners();

        // Request chat history on init
        this.requestChatHistory();
    }

    createChatUI() {
        // Create chat button (floating)
        this.chatButton = document.createElement('button');
        this.chatButton.id = 'chat-button';
        this.chatButton.className = 'chat-button';
        this.chatButton.innerHTML = `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
            <span class="chat-badge" id="chat-badge" style="display: none;">0</span>
        `;
        document.body.appendChild(this.chatButton);

        // Create chat modal
        this.modal = document.createElement('div');
        this.modal.id = 'chat-modal';
        this.modal.className = 'chat-modal';
        this.modal.innerHTML = `
            <div class="chat-container">
                <div class="chat-header">
                    <h3>Session Chat</h3>
                    <span class="chat-session-info">${this.getSessionName()}</span>
                    <button class="chat-close-btn" id="chat-close-btn">&times;</button>
                </div>
                <div class="chat-messages" id="chat-messages">
                    <div class="chat-empty">No messages yet. Start the conversation!</div>
                </div>
                <div class="chat-typing" id="chat-typing" style="display: none;"></div>
                <div class="chat-reply-preview" id="chat-reply-preview" style="display: none;">
                    <span class="reply-label">Replying to:</span>
                    <span class="reply-text" id="reply-text"></span>
                    <button class="reply-cancel" id="reply-cancel">&times;</button>
                </div>
                <div class="chat-input-container">
                    <div class="chat-mention-dropdown" id="chat-mention-dropdown" style="display: none;"></div>
                    <textarea
                        id="chat-input"
                        class="chat-input"
                        placeholder="Type a message... (@ to mention)"
                        maxlength="2000"
                        rows="1"
                    ></textarea>
                    <button id="chat-send-btn" class="chat-send-btn">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="22" y1="2" x2="11" y2="13"></line>
                            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                        </svg>
                    </button>
                </div>
                <div class="chat-char-count" id="chat-char-count">0 / 2000</div>
            </div>
        `;
        document.body.appendChild(this.modal);

        // Inject styles
        this.injectStyles();
    }

    getSessionName() {
        // Try to get session name from URL or page
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('name') || 'Shared Session';
    }

    setupEventListeners() {
        // Chat button click
        this.chatButton.addEventListener('click', () => this.toggleChat());

        // Close button
        document.getElementById('chat-close-btn').addEventListener('click', () => this.closeChat());

        // Send button
        document.getElementById('chat-send-btn').addEventListener('click', () => this.sendMessage());

        // Input handling
        const input = document.getElementById('chat-input');
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                this.sendMessage();
            }
        });

        // Character count and typing indicator
        input.addEventListener('input', () => {
            this.updateCharCount();
            this.autoResizeInput();
            this.handleMentionInput();
            this.sendTypingIndicator(true);
        });

        // Stop typing when input loses focus
        input.addEventListener('blur', () => {
            this.sendTypingIndicator(false);
        });

        // Reply cancel
        document.getElementById('reply-cancel').addEventListener('click', () => this.cancelReply());

        // Close modal on escape
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.closeChat();
            }
        });

        // Close modal on outside click
        this.modal.addEventListener('click', (e) => {
            if (e.target === this.modal) {
                this.closeChat();
            }
        });
    }

    toggleChat() {
        if (this.isOpen) {
            this.closeChat();
        } else {
            this.openChat();
        }
    }

    openChat() {
        this.isOpen = true;
        this.modal.classList.add('open');
        this.chatButton.classList.add('active');

        // Clear unread count
        this.unreadCount = 0;
        this.updateBadge();

        // Mark messages as read
        if (this.messages.length > 0) {
            this.lastReadTimestamp = this.messages[this.messages.length - 1].timestamp;
        }

        // Focus input
        setTimeout(() => {
            document.getElementById('chat-input').focus();
        }, 100);

        // Scroll to bottom
        this.scrollToBottom();
    }

    closeChat() {
        this.isOpen = false;
        this.modal.classList.remove('open');
        this.chatButton.classList.remove('active');
        this.sendTypingIndicator(false);
    }

    requestChatHistory() {
        if (this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({
                type: 'chat_history'
            }));
        }
    }

    handleMessage(data) {
        switch (data.type) {
            case 'chat_message':
                this.onMessageReceived(data);
                break;
            case 'chat_history':
                this.onHistoryReceived(data.messages || []);
                break;
            case 'chat_typing':
                this.onTypingIndicator(data);
                break;
            case 'chat_error':
                this.showError(data.error);
                break;
        }
    }

    onMessageReceived(message) {
        this.messages.push(message);
        this.renderMessage(message);

        // Update unread count if chat is closed and message is from someone else
        if (!this.isOpen && message.user_id !== this.userId) {
            this.unreadCount++;
            this.updateBadge();
            this.playNotificationSound();
        }

        // Scroll to bottom if near bottom
        if (this.isOpen) {
            this.scrollToBottom();
        }

        // Clear typing indicator for this user
        this.typingUsers.delete(message.user_id);
        this.updateTypingIndicator();
    }

    onHistoryReceived(messages) {
        this.messages = messages;
        this.renderAllMessages();

        if (this.messages.length > 0) {
            this.lastReadTimestamp = this.messages[this.messages.length - 1].timestamp;
        }
    }

    onTypingIndicator(data) {
        if (data.user_id === this.userId) return;

        if (data.is_typing) {
            // Clear existing timeout
            const existing = this.typingUsers.get(data.user_id);
            if (existing && existing.timeout) {
                clearTimeout(existing.timeout);
            }

            // Set typing with auto-clear timeout
            this.typingUsers.set(data.user_id, {
                name: data.user_name,
                timeout: setTimeout(() => {
                    this.typingUsers.delete(data.user_id);
                    this.updateTypingIndicator();
                }, 3000)
            });
        } else {
            this.typingUsers.delete(data.user_id);
        }

        this.updateTypingIndicator();
    }

    updateTypingIndicator() {
        const container = document.getElementById('chat-typing');
        if (this.typingUsers.size === 0) {
            container.style.display = 'none';
            return;
        }

        const names = Array.from(this.typingUsers.values()).map(u => u.name);
        let text;
        if (names.length === 1) {
            text = `${names[0]} is typing...`;
        } else if (names.length === 2) {
            text = `${names[0]} and ${names[1]} are typing...`;
        } else {
            text = `${names.length} people are typing...`;
        }

        container.textContent = text;
        container.style.display = 'block';
    }

    renderAllMessages() {
        const container = document.getElementById('chat-messages');
        container.innerHTML = '';

        if (this.messages.length === 0) {
            container.innerHTML = '<div class="chat-empty">No messages yet. Start the conversation!</div>';
            return;
        }

        this.messages.forEach(msg => this.renderMessage(msg, false));
        this.scrollToBottom();
    }

    renderMessage(message, scroll = true) {
        const container = document.getElementById('chat-messages');

        // Remove empty state if present
        const emptyState = container.querySelector('.chat-empty');
        if (emptyState) {
            emptyState.remove();
        }

        const isOwn = message.user_id === this.userId;
        const isMention = message.mention_user_id === this.userId;

        const messageEl = document.createElement('div');
        messageEl.className = `chat-message ${isOwn ? 'own' : ''} ${isMention ? 'mention' : ''}`;
        messageEl.dataset.messageId = message.id;

        // Find reply-to message if exists
        let replyPreview = '';
        if (message.reply_to) {
            const replyMsg = this.messages.find(m => m.id === message.reply_to);
            if (replyMsg) {
                replyPreview = `
                    <div class="message-reply-preview">
                        <span class="reply-author">${this.escapeHtml(replyMsg.user_name)}</span>
                        <span class="reply-content">${this.escapeHtml(replyMsg.message.substring(0, 50))}${replyMsg.message.length > 50 ? '...' : ''}</span>
                    </div>
                `;
            }
        }

        // Format message text with @mentions highlighted
        let formattedMessage = this.escapeHtml(message.message);
        formattedMessage = formattedMessage.replace(/@(\w+)/g, '<span class="mention-text">@$1</span>');

        messageEl.innerHTML = `
            ${replyPreview}
            <div class="message-header">
                <span class="message-author" style="color: ${this.getUserColor(message.user_id)}">${this.escapeHtml(message.user_name)}</span>
                <span class="message-time">${this.formatTime(message.timestamp)}</span>
            </div>
            <div class="message-content">${formattedMessage}</div>
            <div class="message-actions">
                <button class="message-action-btn reply-btn" data-message-id="${message.id}" title="Reply">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="9 17 4 12 9 7"></polyline>
                        <path d="M20 18v-2a4 4 0 0 0-4-4H4"></path>
                    </svg>
                </button>
            </div>
        `;

        // Add reply button listener
        const replyBtn = messageEl.querySelector('.reply-btn');
        replyBtn.addEventListener('click', () => this.setReplyTo(message));

        container.appendChild(messageEl);

        if (scroll) {
            this.scrollToBottom();
        }
    }

    setReplyTo(message) {
        this.replyingTo = message;
        const preview = document.getElementById('chat-reply-preview');
        const text = document.getElementById('reply-text');
        text.textContent = `${message.user_name}: ${message.message.substring(0, 50)}${message.message.length > 50 ? '...' : ''}`;
        preview.style.display = 'flex';
        document.getElementById('chat-input').focus();
    }

    cancelReply() {
        this.replyingTo = null;
        document.getElementById('chat-reply-preview').style.display = 'none';
    }

    sendMessage() {
        const input = document.getElementById('chat-input');
        const text = input.value.trim();

        if (!text || this.ws.readyState !== WebSocket.OPEN) {
            return;
        }

        const messageData = {
            type: 'chat_message',
            message: text,
            reply_to: this.replyingTo?.id || null,
            mention_user_id: this.mentionUser?.user_id || null
        };

        this.ws.send(JSON.stringify(messageData));

        // Clear input and reply
        input.value = '';
        this.cancelReply();
        this.mentionUser = null;
        this.updateCharCount();
        this.autoResizeInput();
        this.sendTypingIndicator(false);
    }

    sendTypingIndicator(isTyping) {
        if (this.ws.readyState !== WebSocket.OPEN) return;

        // Debounce typing indicator
        if (this._typingTimeout) {
            clearTimeout(this._typingTimeout);
        }

        if (isTyping) {
            // Only send if not recently sent
            if (!this._lastTypingSent || Date.now() - this._lastTypingSent > 2000) {
                this.ws.send(JSON.stringify({
                    type: 'chat_typing',
                    is_typing: true
                }));
                this._lastTypingSent = Date.now();
            }

            // Auto-stop after 3 seconds of no input
            this._typingTimeout = setTimeout(() => {
                this.sendTypingIndicator(false);
            }, 3000);
        } else {
            this.ws.send(JSON.stringify({
                type: 'chat_typing',
                is_typing: false
            }));
            this._lastTypingSent = null;
        }
    }

    handleMentionInput() {
        const input = document.getElementById('chat-input');
        const text = input.value;
        const cursorPos = input.selectionStart;

        // Find @ symbol before cursor
        const beforeCursor = text.substring(0, cursorPos);
        const atMatch = beforeCursor.match(/@(\w*)$/);

        if (atMatch) {
            const searchTerm = atMatch[1].toLowerCase();
            this.showMentionDropdown(searchTerm);
        } else {
            this.hideMentionDropdown();
        }
    }

    showMentionDropdown(searchTerm) {
        const dropdown = document.getElementById('chat-mention-dropdown');

        // Get active users from presence manager
        const users = this.getActiveUsers().filter(u =>
            u.user_id !== this.userId &&
            u.user_name.toLowerCase().includes(searchTerm)
        );

        if (users.length === 0) {
            this.hideMentionDropdown();
            return;
        }

        dropdown.innerHTML = users.map(u => `
            <div class="mention-option" data-user-id="${u.user_id}" data-user-name="${u.user_name}">
                <span class="mention-avatar" style="background: ${this.getUserColor(u.user_id)}">${u.user_name.substring(0, 2).toUpperCase()}</span>
                <span class="mention-name">${this.escapeHtml(u.user_name)}</span>
            </div>
        `).join('');

        // Add click handlers
        dropdown.querySelectorAll('.mention-option').forEach(opt => {
            opt.addEventListener('click', () => {
                this.insertMention(opt.dataset.userId, opt.dataset.userName);
            });
        });

        dropdown.style.display = 'block';
    }

    hideMentionDropdown() {
        document.getElementById('chat-mention-dropdown').style.display = 'none';
    }

    insertMention(userId, userName) {
        const input = document.getElementById('chat-input');
        const text = input.value;
        const cursorPos = input.selectionStart;

        // Find and replace the @search with @username
        const beforeCursor = text.substring(0, cursorPos);
        const afterCursor = text.substring(cursorPos);
        const newBefore = beforeCursor.replace(/@\w*$/, `@${userName} `);

        input.value = newBefore + afterCursor;
        input.selectionStart = input.selectionEnd = newBefore.length;

        this.mentionUser = { user_id: userId, user_name: userName };
        this.hideMentionDropdown();
        input.focus();
    }

    getActiveUsers() {
        // Get users from presence manager if available
        if (window.presenceManager && window.presenceManager.activeUsers) {
            return Array.from(window.presenceManager.activeUsers.entries()).map(([id, data]) => ({
                user_id: id,
                user_name: data.name
            }));
        }
        return [];
    }

    updateCharCount() {
        const input = document.getElementById('chat-input');
        const counter = document.getElementById('chat-char-count');
        const count = input.value.length;
        counter.textContent = `${count} / 2000`;
        counter.className = 'chat-char-count' + (count > 1800 ? ' warning' : '');
    }

    autoResizeInput() {
        const input = document.getElementById('chat-input');
        input.style.height = 'auto';
        input.style.height = Math.min(input.scrollHeight, 120) + 'px';
    }

    updateBadge() {
        const badge = document.getElementById('chat-badge');
        if (this.unreadCount > 0) {
            badge.textContent = this.unreadCount > 99 ? '99+' : this.unreadCount;
            badge.style.display = 'flex';
        } else {
            badge.style.display = 'none';
        }
    }

    scrollToBottom() {
        const container = document.getElementById('chat-messages');
        container.scrollTop = container.scrollHeight;
    }

    showError(error) {
        Utils?.notify?.(error, 'error') || alert(error);
    }

    playNotificationSound() {
        // Optional: play a subtle notification sound
        // Could be implemented with Web Audio API
    }

    formatTime(timestamp) {
        const date = new Date(timestamp);
        const now = new Date();
        const isToday = date.toDateString() === now.toDateString();

        if (isToday) {
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } else {
            return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' +
                   date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
    }

    getUserColor(userId) {
        const colors = [
            '#FF5722', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5',
            '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50'
        ];
        let hash = 0;
        for (let i = 0; i < userId.length; i++) {
            hash = userId.charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
    }

    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    destroy() {
        this.sendTypingIndicator(false);
        this.chatButton?.remove();
        this.modal?.remove();

        // Clear typing timeouts
        this.typingUsers.forEach(u => {
            if (u.timeout) clearTimeout(u.timeout);
        });
    }

    injectStyles() {
        if (document.getElementById('chat-styles')) return;

        const style = document.createElement('style');
        style.id = 'chat-styles';
        style.textContent = `
            /* Chat Button */
            .chat-button {
                position: fixed;
                bottom: 24px;
                right: 24px;
                width: 56px;
                height: 56px;
                border-radius: 50%;
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                border: none;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                box-shadow: 0 4px 20px rgba(102, 126, 234, 0.4);
                transition: all 0.3s ease;
                z-index: 9999;
            }

            .chat-button:hover {
                transform: scale(1.1);
                box-shadow: 0 6px 30px rgba(102, 126, 234, 0.6);
            }

            .chat-button.active {
                background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
            }

            .chat-badge {
                position: absolute;
                top: -4px;
                right: -4px;
                background: #ff4444;
                color: white;
                font-size: 11px;
                font-weight: bold;
                min-width: 20px;
                height: 20px;
                border-radius: 10px;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0 6px;
                animation: pulse-badge 2s infinite;
            }

            @keyframes pulse-badge {
                0%, 100% { transform: scale(1); }
                50% { transform: scale(1.1); }
            }

            /* Chat Modal - Non-blocking, positioned in corner */
            .chat-modal {
                position: fixed;
                bottom: 24px;
                right: 24px;
                display: none;
                z-index: 10000;
                pointer-events: none;
            }

            .chat-modal.open {
                display: block;
            }

            .chat-modal .chat-container {
                pointer-events: auto;
            }

            .chat-container {
                width: 400px;
                max-width: calc(100vw - 48px);
                height: 600px;
                max-height: calc(100vh - 100px);
                background: #1e1e2e;
                border-radius: 16px;
                display: flex;
                flex-direction: column;
                box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
                overflow: hidden;
                animation: slideUp 0.3s ease;
            }

            @keyframes slideUp {
                from {
                    opacity: 0;
                    transform: translateY(20px);
                }
                to {
                    opacity: 1;
                    transform: translateY(0);
                }
            }

            /* Header */
            .chat-header {
                padding: 16px 20px;
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                display: flex;
                align-items: center;
                gap: 12px;
            }

            .chat-header h3 {
                margin: 0;
                color: white;
                font-size: 16px;
                font-weight: 600;
            }

            .chat-session-info {
                flex: 1;
                color: rgba(255, 255, 255, 0.7);
                font-size: 12px;
                text-overflow: ellipsis;
                overflow: hidden;
                white-space: nowrap;
            }

            .chat-close-btn {
                background: rgba(255, 255, 255, 0.2);
                border: none;
                color: white;
                width: 32px;
                height: 32px;
                border-radius: 50%;
                cursor: pointer;
                font-size: 20px;
                display: flex;
                align-items: center;
                justify-content: center;
                transition: background 0.2s;
            }

            .chat-close-btn:hover {
                background: rgba(255, 255, 255, 0.3);
            }

            /* Messages */
            .chat-messages {
                flex: 1;
                overflow-y: auto;
                padding: 16px;
                display: flex;
                flex-direction: column;
                gap: 12px;
            }

            .chat-empty {
                color: rgba(255, 255, 255, 0.4);
                text-align: center;
                padding: 40px 20px;
                font-size: 14px;
            }

            .chat-message {
                max-width: 85%;
                padding: 10px 14px;
                background: #2d2d3d;
                border-radius: 12px;
                border-top-left-radius: 4px;
                position: relative;
            }

            .chat-message.own {
                align-self: flex-end;
                background: #3d3d5c;
                border-top-left-radius: 12px;
                border-top-right-radius: 4px;
            }

            .chat-message.mention {
                background: rgba(102, 126, 234, 0.2);
                border-left: 3px solid #667eea;
            }

            .message-header {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 4px;
            }

            .message-author {
                font-size: 12px;
                font-weight: 600;
            }

            .message-time {
                font-size: 10px;
                color: rgba(255, 255, 255, 0.4);
            }

            .message-content {
                color: rgba(255, 255, 255, 0.9);
                font-size: 14px;
                line-height: 1.4;
                word-wrap: break-word;
            }

            .mention-text {
                color: #667eea;
                font-weight: 500;
            }

            .message-reply-preview {
                background: rgba(0, 0, 0, 0.2);
                padding: 6px 10px;
                border-radius: 6px;
                margin-bottom: 8px;
                border-left: 2px solid rgba(255, 255, 255, 0.3);
            }

            .message-reply-preview .reply-author {
                font-size: 11px;
                font-weight: 600;
                color: rgba(255, 255, 255, 0.6);
                display: block;
            }

            .message-reply-preview .reply-content {
                font-size: 12px;
                color: rgba(255, 255, 255, 0.5);
            }

            .message-actions {
                position: absolute;
                right: 8px;
                top: 8px;
                opacity: 0;
                transition: opacity 0.2s;
            }

            .chat-message:hover .message-actions {
                opacity: 1;
            }

            .message-action-btn {
                background: rgba(255, 255, 255, 0.1);
                border: none;
                padding: 4px 6px;
                border-radius: 4px;
                cursor: pointer;
                color: rgba(255, 255, 255, 0.6);
            }

            .message-action-btn:hover {
                background: rgba(255, 255, 255, 0.2);
                color: white;
            }

            /* Typing Indicator */
            .chat-typing {
                padding: 8px 20px;
                color: rgba(255, 255, 255, 0.5);
                font-size: 12px;
                font-style: italic;
            }

            /* Reply Preview */
            .chat-reply-preview {
                padding: 8px 16px;
                background: rgba(102, 126, 234, 0.1);
                border-top: 1px solid rgba(255, 255, 255, 0.1);
                display: flex;
                align-items: center;
                gap: 8px;
            }

            .chat-reply-preview .reply-label {
                color: #667eea;
                font-size: 11px;
                font-weight: 500;
            }

            .chat-reply-preview .reply-text {
                flex: 1;
                color: rgba(255, 255, 255, 0.6);
                font-size: 12px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .chat-reply-preview .reply-cancel {
                background: none;
                border: none;
                color: rgba(255, 255, 255, 0.4);
                cursor: pointer;
                font-size: 16px;
                padding: 4px;
            }

            .chat-reply-preview .reply-cancel:hover {
                color: white;
            }

            /* Input */
            .chat-input-container {
                padding: 12px 16px;
                background: #252535;
                display: flex;
                align-items: flex-end;
                gap: 8px;
                position: relative;
            }

            .chat-input {
                flex: 1;
                background: #1e1e2e;
                border: 1px solid rgba(255, 255, 255, 0.1);
                border-radius: 20px;
                padding: 10px 16px;
                color: white;
                font-size: 14px;
                resize: none;
                outline: none;
                max-height: 120px;
                line-height: 1.4;
            }

            .chat-input:focus {
                border-color: #667eea;
            }

            .chat-input::placeholder {
                color: rgba(255, 255, 255, 0.3);
            }

            .chat-send-btn {
                width: 40px;
                height: 40px;
                border-radius: 50%;
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                border: none;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                transition: transform 0.2s;
            }

            .chat-send-btn:hover {
                transform: scale(1.1);
            }

            .chat-char-count {
                padding: 4px 16px 8px;
                background: #252535;
                color: rgba(255, 255, 255, 0.3);
                font-size: 10px;
                text-align: right;
            }

            .chat-char-count.warning {
                color: #ff6b6b;
            }

            /* Mention Dropdown */
            .chat-mention-dropdown {
                position: absolute;
                bottom: 100%;
                left: 16px;
                right: 60px;
                background: #2d2d3d;
                border-radius: 8px;
                box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
                max-height: 200px;
                overflow-y: auto;
                margin-bottom: 8px;
            }

            .mention-option {
                display: flex;
                align-items: center;
                gap: 10px;
                padding: 10px 14px;
                cursor: pointer;
                transition: background 0.2s;
            }

            .mention-option:hover {
                background: rgba(255, 255, 255, 0.1);
            }

            .mention-avatar {
                width: 28px;
                height: 28px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 11px;
                font-weight: 600;
                color: white;
            }

            .mention-name {
                color: rgba(255, 255, 255, 0.9);
                font-size: 14px;
            }

            /* Scrollbar */
            .chat-messages::-webkit-scrollbar {
                width: 6px;
            }

            .chat-messages::-webkit-scrollbar-track {
                background: transparent;
            }

            .chat-messages::-webkit-scrollbar-thumb {
                background: rgba(255, 255, 255, 0.2);
                border-radius: 3px;
            }

            .chat-messages::-webkit-scrollbar-thumb:hover {
                background: rgba(255, 255, 255, 0.3);
            }
        `;
        document.head.appendChild(style);
    }
}

// Export for use
if (typeof module !== 'undefined' && module.exports) {
    module.exports = CollaborationChat;
}
